/**
 * Tracks metadata about code blocks and scripts.
 * Used for the variable override UI to know which variables are used where.
 */

export interface CodeBlockMeta {
  index: number;
  template: string;
  variables: string[];
}

export interface ScriptMeta {
  name: string;
  template: string;  // Pre-substitution content
  variables: string[];  // Variables used in this script
}

export class MetadataTracker {
  private _codeBlockMeta: CodeBlockMeta[] = [];
  private _scriptMeta: ScriptMeta[] = [];

  // ============ Code Block Metadata ============

  /**
   * Set code block metadata (parsed from source before weaving).
   */
  setCodeBlockMeta(meta: CodeBlockMeta[]): void {
    this._codeBlockMeta = meta;
  }

  /**
   * Get all code block metadata.
   */
  get codeBlockMeta(): CodeBlockMeta[] {
    return this._codeBlockMeta;
  }

  /**
   * Get metadata for a specific code block.
   */
  getCodeBlockMeta(blockIndex: number): CodeBlockMeta | undefined {
    return this._codeBlockMeta.find(m => m.index === blockIndex);
  }

  /**
   * Parse source content to extract code block variable metadata.
   */
  parseCodeBlockVariables(sourceContent: string): CodeBlockMeta[] {
    const codeBlockRegex = /```[\w]*\n([\s\S]*?)```/g;
    const varRegex = /\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g;

    const meta: CodeBlockMeta[] = [];
    let match: RegExpExecArray | null;
    let blockIndex = 0;

    while ((match = codeBlockRegex.exec(sourceContent)) !== null) {
      const blockContent = match[1];
      const usedVars: string[] = [];
      let varMatch: RegExpExecArray | null;

      // Reset regex lastIndex for each block
      varRegex.lastIndex = 0;
      while ((varMatch = varRegex.exec(blockContent)) !== null) {
        if (!usedVars.includes(varMatch[1])) {
          usedVars.push(varMatch[1]);
        }
      }

      if (usedVars.length > 0) {
        meta.push({
          index: blockIndex,
          template: blockContent,
          variables: usedVars
        });
      }
      blockIndex++;
    }

    this._codeBlockMeta = meta;
    return meta;
  }

  // ============ Script Metadata ============

  /**
   * Set script metadata.
   */
  setScriptMeta(meta: ScriptMeta[]): void {
    this._scriptMeta = meta;
  }

  /**
   * Get all script metadata.
   */
  get scriptMeta(): ScriptMeta[] {
    return this._scriptMeta;
  }

  /**
   * Get metadata for a specific script.
   */
  getScriptMeta(scriptName: string): ScriptMeta | undefined {
    return this._scriptMeta.find(m => m.name === scriptName);
  }

  /**
   * Reset all metadata.
   */
  reset(): void {
    this._codeBlockMeta = [];
    this._scriptMeta = [];
  }
}
